import {
    Divider,
    Icon,
    IconButton,
    Text,
    Tr,
    VStack,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { TbTrash } from 'react-icons/tb';
import {
    SettingsInlineEdit,
    SettingsOptionRow,
    SettingsSubTable,
    SettingsTd,
    SettingsTh,
    useSubTablePagination,
} from './primitives';
import { InlineCellDisplay, LocationsPicker } from './UsersInlineCells';
import {
    formatLocationList,
    isSameLocationList,
    resolveLocationList,
    toLocationIds,
    toLocationList,
    type LocationList,
} from './locationListValue';
import { SupplierSearchField } from '../SupplierSearchField';
import type { LocationEntry } from '../../hooks/useGetLocations';
import type { InsuranceDrpSettings } from '../../hooks/useGetSettings';

export interface InlineLocationListCellProps {
    value: LocationList;
    locations: LocationEntry[];
    onSave: (next: LocationList) => void;
    isDisabled?: boolean;
    ariaLabel: string;
}

/**
 * Locations cell for records that store the named-locations shape. Saves on
 * close, since picking several shops takes more than one click. Unlike a user,
 * a record here can legitimately apply to no location at all.
 */
export const InlineLocationListCell = ({
    value,
    locations,
    onSave,
    isDisabled = false,
    ariaLabel,
}: InlineLocationListCellProps) => {
    // What the label counts and what the picker ticks have to agree, so the
    // stored list is read against the org's locations first.
    const resolved = useMemo(() => resolveLocationList(value, locations), [value, locations]);
    const [draft, setDraft] = useState<Array<string> | 'all'>(() => toLocationIds(resolved));

    return (
        <SettingsInlineEdit
            display={
                <InlineCellDisplay
                    label={formatLocationList(resolved)}
                    isMuted={resolved !== 'all' && resolved.length === 0}
                    hasChevron={!isDisabled}
                />
            }
            ariaLabel={ariaLabel}
            isDisabled={isDisabled}
            minW="260px"
            onOpen={() => setDraft(toLocationIds(resolved))}
            onClose={() => {
                const next = toLocationList(draft, locations);
                if (!isSameLocationList(next, resolved)) onSave(next);
            }}
        >
            <LocationsPicker value={draft} locations={locations} onChange={setDraft} />
        </SettingsInlineEdit>
    );
};

/** Price-matching options, as stored on a DRP carrier. */
export interface PriceMatchValue {
    estimate: boolean;
    ccc: boolean;
    partstrader: boolean;
    custom: boolean;
}

/** Estimate is on when CCC, PartsTrader, and custom are all off. */
const withEstimate = (value: PriceMatchValue): PriceMatchValue => {
    const othersOff = !value.ccc && !value.partstrader && !value.custom;
    return othersOff ? { ...value, estimate: true } : value;
};

export const toPriceMatchValue = (item: InsuranceDrpSettings): PriceMatchValue =>
    withEstimate({
        estimate: item.priceMatchEstimate === true,
        ccc: item.priceMatchCCC === true,
        partstrader: item.priceMatchPartstrader === true,
        custom: item.priceMatchCustom === true,
    });

export const formatPriceMatch = (value: PriceMatchValue): string => {
    const sources: string[] = [];
    if (value.custom) sources.push('Custom');
    if (!value.custom && value.ccc) sources.push('CCC');
    if (!value.custom && value.partstrader) sources.push('PartsTrader');
    if (value.estimate) sources.push('Estimate');
    return sources.length > 0 ? sources.join(' + ') : 'Estimate';
};

const isSamePriceMatch = (a: PriceMatchValue, b: PriceMatchValue): boolean =>
    a.estimate === b.estimate && a.ccc === b.ccc && a.partstrader === b.partstrader && a.custom === b.custom;

export interface InlinePriceMatchCellProps {
    value: PriceMatchValue;
    /**
     * Whether the carrier already has a custom ranking. The ranking itself is
     * built on the carrier's edit screen, so without one the custom option has
     * nothing to turn on.
     */
    hasCustomRanking: boolean;
    onSave: (next: PriceMatchValue) => void;
    isDisabled?: boolean;
    ariaLabel: string;
}

/**
 * Price matching for one carrier. CCC and PartsTrader can both be on — we take
 * whichever is cheaper — but a custom ranking replaces them, which is the same
 * rule the carrier's edit screen enforces.
 */
export const InlinePriceMatchCell = ({
    value,
    hasCustomRanking,
    onSave,
    isDisabled = false,
    ariaLabel,
}: InlinePriceMatchCellProps) => {
    const [draft, setDraft] = useState<PriceMatchValue>(value);

    return (
        <SettingsInlineEdit
            display={
                <InlineCellDisplay
                    label={formatPriceMatch(value)}
                    hasChevron={!isDisabled}
                />
            }
            ariaLabel={ariaLabel}
            isDisabled={isDisabled}
            minW="260px"
            onOpen={() => setDraft(value)}
            onClose={() => {
                if (!isSamePriceMatch(draft, value)) onSave(draft);
            }}
        >
            <VStack align="stretch" spacing={0.5}>
                <SettingsOptionRow
                    label="CCC Source Parts"
                    isSelected={draft.ccc}
                    onToggle={() =>
                        setDraft(withEstimate({
                            ...draft,
                            ccc: !draft.ccc,
                            custom: !draft.ccc ? false : draft.custom,
                        }))
                    }
                />
                <SettingsOptionRow
                    label="PartsTrader"
                    isSelected={draft.partstrader}
                    onToggle={() =>
                        setDraft(withEstimate({
                            ...draft,
                            partstrader: !draft.partstrader,
                            custom: !draft.partstrader ? false : draft.custom,
                        }))
                    }
                />
                <Divider my={1.5} />
                <SettingsOptionRow
                    label="Custom ranking"
                    isSelected={draft.custom}
                    isDisabled={!hasCustomRanking}
                    onToggle={() =>
                        setDraft(
                            draft.custom
                                ? withEstimate({ ...draft, custom: false })
                                : { ...draft, ccc: false, partstrader: false, custom: true },
                        )
                    }
                />
                <SettingsOptionRow
                    label="Estimate"
                    isSelected={draft.estimate || (!draft.ccc && !draft.partstrader && !draft.custom)}
                    isDisabled={!draft.ccc && !draft.partstrader && !draft.custom}
                    onToggle={() => {
                        if (!draft.ccc && !draft.partstrader && !draft.custom) return;
                        setDraft({ ...draft, estimate: !draft.estimate });
                    }}
                />
                <Text fontSize="xs" color="gray.500" px={3} pt={1.5}>
                    {hasCustomRanking
                        ? 'A custom ranking replaces CCC and PartsTrader matching.'
                        : 'Build a custom ranking on the carrier’s edit screen to use this.'}
                </Text>
            </VStack>
        </SettingsInlineEdit>
    );
};

/** Rows of prohibited vendors shown at once inside the editor panel. */
const PROHIBITED_PAGE_SIZE = 5;

/** A vendor a carrier prohibits, in the shape the record stores. */
export interface ProhibitedVendor {
    _id: string;
    title: string;
}

export const formatProhibitedVendors = (value: ProhibitedVendor[]): string => {
    if (value.length === 0) return 'None';
    if (value.length === 1) return value[0].title;
    return `${value.length} vendors`;
};

const isSameProhibited = (a: ProhibitedVendor[], b: ProhibitedVendor[]): boolean => {
    if (a.length !== b.length) return false;
    const other = new Set(b.map((entry) => entry._id));
    return a.every((entry) => other.has(entry._id));
};

export interface InlineProhibitedVendorsCellProps {
    value: ProhibitedVendor[];
    onSave: (next: ProhibitedVendor[]) => void;
    isDisabled?: boolean;
    ariaLabel: string;
}

/**
 * Which vendors a carrier will not accept parts from. Search the supplier
 * directory to add one and remove it from the list below, the same way the
 * carrier's edit screen works — the set is open-ended, so there is no fixed
 * list of options to check off.
 */
export const InlineProhibitedVendorsCell = ({
    value,
    onSave,
    isDisabled = false,
    ariaLabel,
}: InlineProhibitedVendorsCellProps) => {
    const [draft, setDraft] = useState<ProhibitedVendor[]>(value);
    // Long lists page rather than growing the panel past the viewport.
    const { currentPage, setCurrentPage, totalPages, pageItems } = useSubTablePagination(
        draft,
        PROHIBITED_PAGE_SIZE,
    );

    return (
        <SettingsInlineEdit
            display={
                <InlineCellDisplay
                    label={formatProhibitedVendors(value)}
                    isMuted={value.length === 0}
                    hasChevron={!isDisabled}
                />
            }
            ariaLabel={ariaLabel}
            isDisabled={isDisabled}
            minW="280px"
            onOpen={() => {
                setDraft(value);
            }}
            onClose={() => {
                if (!isSameProhibited(draft, value)) onSave(draft);
            }}
        >
            <VStack align="stretch" spacing={2}>
                <SupplierSearchField
                    autoFocus
                    placeholder="Search vendors to prohibit"
                    emptyMessage="No vendors found"
                    excludeIds={draft.map((entry) => entry._id)}
                    onSelect={(supplier) => {
                        setDraft((prev) => [
                            ...prev,
                            { _id: supplier._id, title: supplier.title },
                        ]);
                    }}
                />

                {draft.length === 0 ? (
                    <Text fontSize="xs" color="gray.500">
                        No prohibited vendors — parts from any vendor are allowed.
                    </Text>
                ) : (
                    <SettingsSubTable
                        head={
                            <>
                                <SettingsTh>VENDOR</SettingsTh>
                                <SettingsTh w="44px" aria-label="Remove" />
                            </>
                        }
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    >
                        {pageItems.map((entry) => (
                            <Tr key={entry._id}>
                                <SettingsTd>
                                    <Text fontSize="sm" color="gray.700" noOfLines={1}>
                                        {entry.title}
                                    </Text>
                                </SettingsTd>
                                <SettingsTd textAlign="right">
                                    <IconButton
                                        aria-label={`Remove ${entry.title}`}
                                        icon={
                                            <Icon
                                                as={TbTrash as React.ElementType}
                                                boxSize={4}
                                            />
                                        }
                                        size="xs"
                                        variant="ghost"
                                        colorScheme="red"
                                        onClick={() =>
                                            setDraft((prev) =>
                                                prev.filter((current) => current._id !== entry._id),
                                            )
                                        }
                                    />
                                </SettingsTd>
                            </Tr>
                        ))}
                    </SettingsSubTable>
                )}
            </VStack>
        </SettingsInlineEdit>
    );
};
